import React, { useEffect, useRef, useState } from "react";
import ConversationMessageRecord from "models/view-models/conversations/conversation-message-record";
import Button, { ButtonTypes } from "atoms/buttons/button";
import { ButtonStyles } from "atoms/constants/button-styles";
import { ButtonSizes } from "atoms/constants/button-sizes";
import TextArea from "atoms/forms/text-area";
import uuid from "uuid";
import Form from "molecules/forms/form";
import useBreakpoint from "utilities/hooks/use-breakpoint";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import { t } from "utilities/localization-utils";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface ChatMessageEditorProps {
    message: ConversationMessageRecord;
    onClose: (saveChanges: boolean) => void;
    onMessageChange: (updatedMessage: ConversationMessageRecord) => void;
}

// #endregion Interfaces
// -----------------------------------------------------------------------------------------

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const BASE_CLASS = "c-chat-modal-message-editor";

// #endregion Constants
// -----------------------------------------------------------------------------------------

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

export const ChatMessageEditor: React.FC<ChatMessageEditorProps> = ({
    message,
    onClose,
    onMessageChange,
}) => {
    const [defaultId] = useState(`chat-message-editor-${uuid.v4()}`);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const [intialSelectionSize] = useState(message.content?.length ?? 0);
    const isTabletOrBigger = useBreakpoint(Breakpoints.Tablet);

    // Handle Enter/Shift+Enter in textarea
    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (message.content?.trim()) {
                onClose(true);
            }
        }

        if (e.key === "Escape") {
            e.preventDefault();
            onClose(false);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (message.content?.trim()) {
            onClose(true);
        }
    };

    // Initial focus and selection
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.setSelectionRange(0, intialSelectionSize);
        }
    }, [intialSelectionSize]);

    return (
        <div className={BASE_CLASS}>
            <Form onSubmit={handleSubmit}>
                <div className={`${BASE_CLASS}__input-wrapper`}>
                    <TextArea
                        ref={inputRef}
                        id={defaultId}
                        onChange={(e) =>
                            onMessageChange(
                                message.with({ content: e.target.value })
                            )
                        }
                        onKeyDown={handleKeyDown}
                        value={message.content}
                        rows={isTabletOrBigger ? 4 : 2}
                        spellcheck={false}
                    />
                    <div className={`${BASE_CLASS}__editor-buttons`}>
                        <Button
                            type={ButtonTypes.Button}
                            style={ButtonStyles.Tertiary}
                            size={ButtonSizes.Small}
                            accessibleText="Cancel"
                            onClick={() => onClose(false)}>
                            {t("close")}
                            {/* Comment out the icon for now. Looked funny with the buttons. */}
                            {/* <Icon size={IconSizes.Medium} type={Icons.Close} /> */}
                        </Button>
                        <Button
                            type={ButtonTypes.Submit}
                            style={ButtonStyles.Primary}
                            size={ButtonSizes.Small}
                            title="Send"
                            accessibleText="Send">
                            {t("send")}
                            {/* Comment out the icon for now. Looked funny with the buttons. */}
                            {/* <Icon size={IconSizes.Medium} type={Icons.Checkmark} /> */}
                        </Button>
                    </div>
                </div>
            </Form>
        </div>
    );
};

// #endregion Component
// -----------------------------------------------------------------------------------------
